/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/alt-text */
// MeetingCancelDeclineModal.jsx — Cancel (organiser) / Decline (participant)
// flow for a meeting on the side-by-side room view. Mirrors WeeklyCalendarEvent:
//   one-off    -> single button, Select 1 with the meeting's StartDate
//   recurring  -> "this occurrence": pick a date, Select 2 with that date
//                 "the series":      Select 1 with the meeting's StartDate
// `onSubmit(select, date)` performs the API call; this component only decides
// which select/date to send.
import { useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import { Images } from "../Common/Images.jsx";
import { formatDateToEEEddMMM, formatDateYYYYMMDD } from "../../utils/CommonUtils.js";

export const SELECT_WHOLE = 1;      // one-off meeting, or the whole series
export const SELECT_OCCURRENCE = 2; // a single occurrence of a series

const toDateObject = (yyyyMMdd) => {
    if (!yyyyMMdd) return null;
    const [y, m, d] = String(yyyyMMdd).split("-").map(Number);
    return new Date(y, m - 1, d);
};

// Same rules as the weekly date-select modals: never a holiday; when the series
// lists its days, only those; otherwise StartDate..LastDateOfBookingAvailable.
export const isOccurrenceDateAllowed = (dateStr, { holidays = [], selectedDays = [], startDate, lastDateOfBookingAvailable }) => {
    if (holidays.includes(dateStr)) return false;
    if (selectedDays.length > 0) return selectedDays.includes(dateStr);
    if (lastDateOfBookingAvailable && startDate) {
        return dateStr >= startDate && dateStr <= lastDateOfBookingAvailable;
    }
    return true;
};

function MeetingCancelDeclineModal({
    mode,               // "cancel" | "decline"
    meeting,
    selectedDate,
    userName,
    holidays = [],
    locale,
    isLoading,
    onClose,
    onSubmit,
}) {
    const { t } = useTranslation("common");
    const [step, setStep] = useState("confirm"); // "confirm" | "pickDate"

    const booking = meeting.booking || {};
    const bookingDateStr = formatDateYYYYMMDD(selectedDate);
    const startDate = booking.StartDate || bookingDateStr;
    const isRecurring = Number(booking.MeetingRepeat) > 0;
    const isCancel = mode === "cancel";

    const titleText = isCancel ? t("cancel") : t("react_decline");
    const occurrenceLabel = isCancel ? t("react_cancel_this_occurrence") : t("react_decline_occurrence");
    const seriesLabel = isCancel ? t("react_cancel_the_series") : t("react_decline_series");

    const selectedDays = Array.isArray(booking.SelectedDays) ? [...booking.SelectedDays].sort() : [];
    const pickerRules = {
        holidays,
        selectedDays,
        startDate,
        lastDateOfBookingAvailable: booking.LastDateOfBookingAvailable,
    };

    const repeatOption = booking.RepeatOption;
    const repeatOptionDay = booking.RepeatOptionDay;

    const submit = (select, date) => {
        if (!isLoading) onSubmit(select, date);
    };

    const renderShell = (body) => (
        <div>
            <div
                className="modal"
                style={{ display: "block", backgroundColor: "rgba(217, 217, 217, 0.59)" }}
                data-bs-backdrop="static"
                data-bs-keyboard="false"
            >
                <div className="modal-dialog modal-dialog-centered modal-custom-width">
                    <div className="modal-content modal-radius">
                        <div className="modal-body">
                            <div className="close-modal">
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onClose();
                                    }}
                                >
                                    <img src={Images.Close} aria-label="Close" />
                                </a>
                            </div>
                            {body}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (step === "pickDate") {
        return renderShell(
            <>
                <div className="pt-4">
                    <h5 className="popup-title color-pblack">
                        {titleText} <span className="color-green">{meeting.meetingName}</span>
                    </h5>
                </div>
                <div className="divider-line mt-3 mb-3"></div>
                <div className="hidden-datepicker-input my-3">
                    <DatePicker
                        locale={locale}
                        selected={isCancel ? selectedDate : null}
                        openToDate={selectedDate}
                        minDate={toDateObject(selectedDays[0])}
                        maxDate={toDateObject(selectedDays[selectedDays.length - 1])}
                        inline
                        filterDate={(date) => isOccurrenceDateAllowed(formatDateYYYYMMDD(date), pickerRules)}
                        onChange={(date) => submit(SELECT_OCCURRENCE, formatDateYYYYMMDD(date))}
                        dayClassName={(date) =>
                            holidays.includes(formatDateYYYYMMDD(date)) ? "react-datepicker__day--holidayClass" : undefined
                        }
                    />
                </div>
            </>
        );
    }

    return renderShell(
        <>
            <div className="pt-4">
                <h5 className="popup-title color-pblack">
                    {titleText} <span className="color-green">{meeting.meetingName}{isCancel ? "?" : ""}</span>
                </h5>
            </div>

            <div className="divider-line mt-3"></div>
            <div className="modal-details">
                <div className="icon-with-list">
                    <div className="icon-wrap">
                        <img src={Images.Participant} />
                        <h4>{userName}</h4>
                    </div>
                    <div className="icon-wrap">
                        <img src={Images.GreenCalender} alt="repeat" />
                        <h4>{formatDateToEEEddMMM(bookingDateStr)}</h4>
                    </div>
                    <div className="icon-wrap">
                        {repeatOption && <img src={Images.RepeatEventClock} alt="repeat" />}
                        <h4>
                            {t("from")} <span> {meeting.startTime}</span> {t("to")} <span> {meeting.endTime}</span>
                        </h4>
                    </div>
                    <div className="icon-wrap">
                        <h4 className="ps-3 ms-2">
                            {repeatOption && repeatOption !== "No repeat" ? repeatOption : ""}{" "}
                            {repeatOptionDay && repeatOptionDay !== "0" ? `(${repeatOptionDay})` : ""}
                        </h4>
                    </div>
                </div>
            </div>

            {isRecurring ? (
                <div className="mb-4">
                    <button
                        type="button"
                        className="main-btn fill-green-btn w-100"
                        onClick={() => !isLoading && setStep("pickDate")}
                    >
                        {occurrenceLabel}
                    </button>
                    <button
                        type="button"
                        className="main-btn fill-green-btn w-100 mt-2"
                        onClick={() => submit(SELECT_WHOLE, startDate)}
                    >
                        {seriesLabel}
                    </button>
                </div>
            ) : (
                <div className="mb-4">
                    <button
                        type="button"
                        className="main-btn fill-green-btn w-100"
                        disabled={isLoading}
                        onClick={() => submit(SELECT_WHOLE, startDate)}
                    >
                        {titleText}
                    </button>
                </div>
            )}
        </>
    );
}

export default MeetingCancelDeclineModal;
